import { Trans, useLingui } from "@lingui/react/macro";
import { XIcon } from "lucide-react";
import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { Popover, PopoverAnchor, PopoverClose, PopoverContent } from "@/shared/ui/popover";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/shared/ui/sheet";

import { useNarrowScreen } from "./useNarrowScreen";

// Через сколько показать описание при наведении и спрятать, когда курсор ушёл: без задержки
// карточки мигают, пока ведёшь мышью по ряду иконок.
const HOVER_OPEN_MS = 120;
const HOVER_CLOSE_MS = 180;

export type InfoCardParts = {
  // Заголовок карточки: в шторке это заголовок диалога (`SheetTitle`).
  renderTitle: (title: string) => ReactNode;
  // Крестик справа от заголовка — только во всплывающем окне.
  closeButton?: ReactNode;
};

type InfoPopoverProps = {
  // Подпись кнопки для экранного диктора: «Описание предмета Healbane».
  label: string;
  // Название окна с описанием.
  title: string;
  // Иконка: становится кнопкой.
  children: ReactNode;
  renderCard: (parts: InfoCardParts) => ReactNode;
  className?: string;
};

// Иконка, которая показывает описание (предмета или умения): на компьютере — окном у иконки
// при наведении, нажатие его закрепляет; на телефоне — шторкой снизу по нажатию.
// Закрывается крестиком, «Закрыть», кликом мимо и Esc.
export const InfoPopover = ({
  label,
  title,
  children,
  renderCard,
  className,
}: InfoPopoverProps) => {
  const { t } = useLingui();
  const narrow = useNarrowScreen();
  const [open, setOpen] = useState(false);
  // Открыто нажатием (или с клавиатуры) — не закрывается, когда курсор уходит.
  const [pinned, setPinned] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement>(null);
  useEffect(
    () => () => {
      window.clearTimeout(timer.current);
    },
    [],
  );

  const trigger = (
    <button
      ref={triggerRef}
      type="button"
      aria-label={label}
      className={cn(
        "inline-flex shrink-0 cursor-pointer rounded-lg outline-none hover:brightness-110 focus-visible:ring-2 focus-visible:ring-accent/70",
        className,
      )}
    >
      {children}
    </button>
  );

  if (narrow) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          aria-describedby={undefined}
          className="px-4 pt-2.5 pb-5"
        >
          <span
            aria-hidden="true"
            className="mx-auto mb-3 h-1 w-10 shrink-0 rounded-full bg-border-strong"
          />
          {open &&
            renderCard({
              renderTitle: (text) => (
                <SheetTitle className="font-sans text-[17px] leading-tight font-semibold">
                  {text}
                </SheetTitle>
              ),
            })}
          <SheetClose asChild>
            <Button variant="outline" className="mt-4 min-h-11 w-full text-accent">
              <Trans>Close</Trans>
            </Button>
          </SheetClose>
        </SheetContent>
      </Sheet>
    );
  }

  const later = (action: () => void, ms: number) => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(action, ms);
  };
  // Наведение — только мышью: на сенсорном экране окно открывает нажатие.
  const enter = (event: PointerEvent) => {
    if (event.pointerType === "mouse") {
      later(
        () => {
          setOpen(true);
        },
        open ? 0 : HOVER_OPEN_MS,
      );
    }
  };
  const leave = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && !pinned) {
      later(() => {
        setOpen(false);
      }, HOVER_CLOSE_MS);
    }
  };
  const close = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
    setPinned(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <PopoverAnchor asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-label={label}
          aria-expanded={open}
          aria-haspopup="dialog"
          onPointerEnter={enter}
          onPointerLeave={leave}
          onClick={() => {
            window.clearTimeout(timer.current);
            if (open && pinned) {
              close();
            } else {
              setPinned(true);
              setOpen(true);
            }
          }}
          className={cn(
            "inline-flex shrink-0 cursor-pointer rounded-lg outline-none hover:brightness-110 focus-visible:ring-2 focus-visible:ring-accent/70",
            className,
          )}
        >
          {children}
        </button>
      </PopoverAnchor>
      <PopoverContent
        aria-label={title}
        collisionPadding={16}
        className="w-[340px]"
        onPointerEnter={enter}
        onPointerLeave={leave}
        // Открыто наведением — фокус остаётся на странице, иначе окно крадёт его у курсора.
        onOpenAutoFocus={(event) => {
          if (!pinned) {
            event.preventDefault();
          }
        }}
        // Нажатие на свою же иконку — не «клик мимо»: его обработает сама иконка.
        onPointerDownOutside={(event) => {
          if (triggerRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
      >
        {renderCard({
          renderTitle: (text) => (
            <h3 className="text-[17px] leading-tight font-semibold">{text}</h3>
          ),
          closeButton: (
            <PopoverClose
              aria-label={t`Close`}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-accent/60"
            >
              <XIcon aria-hidden="true" className="size-4" />
            </PopoverClose>
          ),
        })}
      </PopoverContent>
    </Popover>
  );
};
